import styled from "styled-components";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import ScrollableTable from "@/components/ScrollableTable";
import { Table, TableContent, TableHeader, Th } from "@/components/Table";
import { SrOnly } from "@/components/Layout/Parts";
import { LargeTh } from "@/components/Table";
import PreProductionTableRow from "./PreProductionTableRow";
import Text from "@/components/Text";
import { ProductionProjectResource } from "@/types/resources";
import { api } from "@/utils/api";
import useDebounce from "@/hooks/useDebounce";
import SearchBar from "@/components/SearchBar";

export const StyledTable = styled(Table)`
  th,
  td {
    padding: 16px 12px;
    font-size: 11px;
    vertical-align: top;
    &:nth-child(1) {
      width: 216px;
      min-width: 216px;
      max-width: 216px;
      padding-left: 24px;
      background-color: ${({ theme }) => theme.colors.white};
      position: sticky;
      top: 0;
      left: 0;
      z-index: 1;
    }
    &:last-child {
      width: 56px;
      padding-right: 20px;
      position: relative;
    }
  }
`;

const Heading = styled(Text)`
  text-transform: uppercase;
  margin-top: 12px;
`;

const Holder = styled.div`
  margin-bottom: 28px;
`;

const PreProductionTable = () => {
  const { teamId } = useParams();

  const [search, setSearch] = useState("");

  const debouncedSearch = useDebounce(search, 500);

  const {
    data: projects,
    isError,
    isLoading,
  } = useQuery<ProductionProjectResource[]>(
    [
      "production_projects",
      { teamId, ...(!!debouncedSearch && { debouncedSearch }) },
    ],
    () =>
      api.get<never, ProductionProjectResource[]>("/v1/production/projects", {
        params: {
          "filter[team_id]": teamId,
          ...(!!debouncedSearch && { "filter[search]": debouncedSearch }),
        },
      })
  );

  return (
    <>
      <SearchBar value={search} setValue={setSearch} />
      <Holder>
        <TableHeader>
          <Heading $type="smallHeadline" $color="gray">
            Ongoing
          </Heading>
        </TableHeader>
        <ScrollableTable>
          <StyledTable>
            <thead>
              <tr>
                <Th>
                  Project Name
                  <br />
                  <Text as="span" $color="gray" $type="extraSmall">
                    Project owner
                  </Text>
                </Th>
                <Th>Number of Scenes</Th>
                <Th>Channel</Th>
                <LargeTh>Director</LargeTh>
                <Th>Project ID</Th>
                <LargeTh>Topics</LargeTh>
                <Th>
                  <SrOnly>Actions</SrOnly>
                </Th>
              </tr>
            </thead>
            <tbody>
              <TableContent
                isLoading={isLoading}
                isError={isError}
                hasData={!!projects?.length}
              >
                {projects?.map((project) => (
                  <PreProductionTableRow key={project.id} project={project} />
                ))}
              </TableContent>
            </tbody>
          </StyledTable>
        </ScrollableTable>
      </Holder>
    </>
  );
};

export default PreProductionTable;
